import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { MdArrowBack, MdEdit, MdFolderOpen, MdVerifiedUser } from 'react-icons/md';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import {
  AppCard,
  FileViewerModal,
  OutlineButton,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { resolveAssetOwner } from '../../../../data/master/assetOwner';
import {
  getEquipment,
  getEquipmentIndentHistory,
  listEquipment,
} from '../../../../repositories/equipment.repository';
import { listDocuments } from '../../../../repositories/document.repository';
import { loadByIdOrCode } from '../../../../utils/entityResolve';
import { toUiStatus } from '../../../../models/_helpers';
import { EquipmentDetailContent } from '../shared/EquipmentDetailContent';
import { EquipmentDocumentsModal } from './EquipmentDocumentsModal';
import { EquipmentEditModal } from './EquipmentEditModal';
import { EquipmentKycStatusModal } from './EquipmentKycStatusModal';

function mapAssetDocs(docs) {
  return (Array.isArray(docs) ? docs : []).map((d) => ({
    id: d.id,
    docType: d.docType || '',
    mimeType: d.mimeType || '',
    group:
      d.docType?.includes('side') || /front|rear|left|right/i.test(d.docType || '')
        ? 'sides'
        : /puc|insurance|fitness/i.test(d.docType || '')
          ? 'compliance'
          : 'other',
    label: d.docType || 'Document',
    fileName: d.fileName || '',
    kind: (d.mimeType || '').includes('pdf') ? 'pdf' : 'image',
    url: d.url || null,
  }));
}

function useEquipmentDetailBack() {
  const location = useLocation();
  const villagerMatch = useMatch(
    '/admin/equipments/villagers/:peopleId/assets/:assetId',
  );
  const vendorMatch = useMatch(
    '/admin/equipments/vendors/:vendorId/assets/:assetId',
  );

  if (villagerMatch?.params?.peopleId) {
    return {
      to: `/admin/equipments/villagers/${villagerMatch.params.peopleId}`,
      label: 'Back to villager',
    };
  }
  if (vendorMatch?.params?.vendorId) {
    return {
      to: `/admin/equipments/vendors/${vendorMatch.params.vendorId}`,
      label: 'Back to vendor',
    };
  }

  const from = location.state?.from;
  if (typeof from === 'string' && from.startsWith('/admin/')) {
    if (from.includes('/indents')) {
      return { to: from, label: 'Back to indent' };
    }
    if (from.includes('/villagers')) {
      return { to: from, label: 'Back to villager' };
    }
    if (from.includes('/vendors')) {
      return { to: from, label: 'Back to vendor' };
    }
    return { to: from, label: 'Back' };
  }

  return { to: '/admin/equipments/fleet', label: 'Back to fleet' };
}

export default function EquipmentFleetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const backNav = useEquipmentDetailBack();
  const toast = useToast();
  const kycModal = useDisclosure();
  const editModal = useDisclosure();
  const documentsModal = useDisclosure();
  const fileViewer = useDisclosure();
  const [viewerFile, setViewerFile] = useState(null);
  const [asset, setAsset] = useState(null);
  const [indentHistory, setIndentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documentsGroup, setDocumentsGroup] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await loadByIdOrCode({
        param: assetId,
        listFn: listEquipment,
        getFn: getEquipment,
        codeKey: 'assetId',
      });
      if (loaded?.id) {
        const [docs, historyRows] = await Promise.all([
          listDocuments({
            ownerType: 'asset',
            ownerId: loaded.id,
          }).catch(() => []),
          getEquipmentIndentHistory(loaded.id).catch(() => []),
        ]);
        setAsset({
          ...loaded,
          documents: mapAssetDocs(docs),
        });
        setIndentHistory(
          (historyRows || []).map((h) => ({
            ...h,
            status: h.status ? toUiStatus(h.status) : '',
          })),
        );
      } else {
        setAsset(loaded);
        setIndentHistory([]);
      }
    } catch (err) {
      toast({
        title: err?.message || 'Failed to load equipment',
        status: 'error',
        position: 'top-right',
      });
      setAsset(null);
    } finally {
      setLoading(false);
    }
  }, [assetId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const owner = useMemo(() => resolveAssetOwner(asset), [asset]);

  const reloadDocuments = useCallback(async () => {
    if (!asset?.id) return;
    const docs = await listDocuments({
      ownerType: 'asset',
      ownerId: asset.id,
    }).catch(() => []);
    setAsset((prev) => (prev ? { ...prev, documents: mapAssetDocs(docs) } : prev));
  }, [asset?.id]);

  const openDoc = (doc) => {
    if (!doc?.url) return;
    setViewerFile(doc);
    fileViewer.onOpen();
  };

  const openManageDocuments = useCallback(
    (group = null) => {
      setDocumentsGroup(group);
      documentsModal.onOpen();
    },
    [documentsModal],
  );

  const goBack = () => navigate(backNav.to);

  const kycStatus = asset?.status || 'Pending';

  if (loading) {
    return <PageLoader label="Loading equipment…" />;
  }

  if (!asset) {
    return (
      <Box>
        <OutlineButton size="sm" leftIcon={<MdArrowBack />} mb={4} onClick={goBack}>
          {backNav.label}
        </OutlineButton>
        <AppCard py={12} textAlign="center">
          <Text fontWeight="600" fontSize={{ base: '16px', xl: '18px' }}>
            Equipment not found
          </Text>
          <Text mt={2} color="text.secondary" fontSize={{ base: '14px', xl: '15px' }}>
            No asset {assetId} in the fleet register.
          </Text>
        </AppCard>
      </Box>
    );
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        <Box>
          <OutlineButton size="sm" leftIcon={<MdArrowBack />} mb={3} onClick={goBack}>
            {backNav.label}
          </OutlineButton>
          <Heading size={{ base: 'lg', xl: 'xl' }}>
            {asset.equipmentDescription || asset.equipmentType}
            {asset.model ? ` · ${asset.model}` : ''}
          </Heading>
          <Flex align="center" gap={3} mt={2} wrap="wrap">
            <Text color="text.secondary" fontFamily="mono" fontSize={{ base: '14px', xl: '15px' }}>
              {asset.assetId}
            </Text>
            <StatusBadge status={kycStatus === 'Verified' ? 'verified' : kycStatus === 'Rejected' ? 'error' : 'pending'}>
              KYC: {kycStatus}
            </StatusBadge>
            <StatusBadge
              status={asset.operationalStatus === 'Deployed' ? 'warning' : 'verified'}
            >
              {asset.operationalStatus || 'Available'}
            </StatusBadge>
            <StatusBadge status={owner.ownerType === 'Vendor' ? 'info' : 'pending'}>
              {owner.ownerType}
            </StatusBadge>
          </Flex>
          <Text mt={2} fontSize={{ base: '14px', xl: '16px' }} color="text.secondary">
            {asset.regNo}
            {owner.villageName ? ` · ${owner.villageName}` : ''}
          </Text>
        </Box>
        <HStack spacing={2} align="start" wrap="wrap">
          <OutlineButton
            size="sm"
            leftIcon={<MdVerifiedUser />}
            onClick={kycModal.onOpen}
          >
            Update KYC
          </OutlineButton>
          <PrimaryButton size="sm" leftIcon={<MdEdit />} onClick={editModal.onOpen}>
            Edit equipment
          </PrimaryButton>
          <OutlineButton
            size="sm"
            leftIcon={<MdFolderOpen />}
            onClick={() => openManageDocuments(null)}
          >
            All documents
          </OutlineButton>
        </HStack>
      </Flex>

      {kycStatus === 'Pending' ? (
        <Box mb={6}>
          <StatusAlertCard
            title="KYC pending"
            body="This equipment is awaiting KYC review. Use Update KYC to mark Verified or Rejected."
            icon={MdVerifiedUser}
          />
        </Box>
      ) : null}

      <EquipmentDetailContent
        asset={asset}
        onManageDocuments={openManageDocuments}
        onOpenDocument={openDoc}
        onDocumentsChanged={reloadDocuments}
        indentHistory={indentHistory}
      />

      <EquipmentKycStatusModal
        isOpen={kycModal.isOpen}
        onClose={kycModal.onClose}
        asset={asset}
        onSaved={load}
      />
      <EquipmentEditModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        asset={asset}
        onSaved={load}
      />
      <EquipmentDocumentsModal
        isOpen={documentsModal.isOpen}
        onClose={documentsModal.onClose}
        asset={asset}
        initialGroup={documentsGroup}
        onSaved={reloadDocuments}
      />

      <FileViewerModal
        isOpen={fileViewer.isOpen}
        onClose={() => {
          fileViewer.onClose();
          setViewerFile(null);
        }}
        file={viewerFile}
      />
    </Box>
  );
}
